import { SxProps, Theme } from "@mui/material";

export const roleplayPhoneLandscapeMedia =
  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)";

export const containerStyles = (
  showCountdown: boolean,
  lessonDialogue: boolean
): SxProps<Theme> => (theme) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor:
    theme.palette.mode === "dark" ? "#000" : theme.palette.background.default,
  borderRadius: 0,
  overflow: "hidden",
});

// Split layout: video + session context (roleplay / coaching video).
export const sessionLayoutRootStyles: SxProps<Theme> = (theme) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  backgroundColor:
    theme.palette.mode === "dark" ? "#05070B" : theme.palette.grey[100],
  overflow: "hidden",
  minHeight: 0,
  [roleplayPhoneLandscapeMedia]: {
    flexDirection: "row",
    alignItems: "stretch",
  },
});

export const videoColumnStyles: SxProps<Theme> = (theme) => ({
  position: "relative",
  flex: { xs: "1 1 42%", sm: "1 1 48%", md: "1 1 62%" },
  minWidth: 0,
  minHeight: { xs: 0, sm: 160, md: 0 },
  maxHeight: { xs: "42%", sm: "50%", md: "none" },
  display: "flex",
  flexDirection: "column",
  backgroundColor:
    theme.palette.mode === "dark" ? "#05070B" : theme.palette.grey[100],
  // Inset the stage card so top/bottom aren't flush solid bars.
  px: { xs: 1.25, sm: 1.5, md: 2 },
  py: { xs: 1, sm: 1.25, md: 2 },
  gap: 0,
  [roleplayPhoneLandscapeMedia]: {
    flex: "0 0 42%",
    width: "42%",
    maxWidth: 360,
    maxHeight: "none",
    minHeight: 0,
    px: 1,
    py: 0.5,
  },
});

//Highlighted stage card (video + controls) — matches audio THE STAGE blocks.
export const videoStagePanelStyles = (theme: Theme) => ({
  flex: "1 1 auto",
  minHeight: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: { xs: 0.75, sm: 1 },
  borderRadius: 2,
  padding: { xs: 1, sm: 1.25, md: 1.5 },
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "#FFFFFF",
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.14)"
      : "rgba(15, 23, 42, 0.14)"
  }`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 1px 3px rgba(15, 23, 42, 0.05)",
  overflow: "hidden",
});

// Rounded video frame (photo-2 style). Video surface stays dark.
export const videoStageStyles: SxProps<Theme> = (theme) => ({
  position: "relative",
  flex: "1 1 auto",
  minHeight: 0,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  backgroundColor:
    theme.palette.mode === "dark" ? "#0B0F14" : "#111827",
  borderRadius: { xs: "12px", sm: "16px", md: "18px" },
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(15,23,42,0.08)"
  }`,
});

// Mic waves overlaid at the bottom inside the rounded video (no plate).
export const inVideoWaveOverlayStyles: SxProps<Theme> = {
  position: "absolute",
  left: "50%",
  bottom: { xs: 10, sm: 14, md: 18 },
  transform: "translateX(-50%)",
  width: { xs: "calc(100% - 24px)", sm: 360, md: 440 },
  maxWidth: "calc(100% - 24px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: { xs: 36, sm: 42 },
  zIndex: 12,
  pointerEvents: "none",
  background: "transparent",
};

export const videoTimerOverlayStyles: SxProps<Theme> = {
  position: "absolute",
  top: { xs: 8, sm: 12, md: 16 },
  right: { xs: 8, sm: 12, md: 16 },
  zIndex: 1000,
  [roleplayPhoneLandscapeMedia]: {
    top: 6,
    right: 6,
  },
};

// Compact mic/end bar under the video (no tall black slab).
export const dockedControlsStyles: SxProps<Theme> = {
  position: "relative",
  flexShrink: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  px: { xs: 0.5, sm: 1 },
  pt: 0,
  pb: {
    xs: "max(4px, env(safe-area-inset-bottom, 0px))",
    sm: "max(6px, env(safe-area-inset-bottom, 0px))",
  },
  backgroundColor: "transparent",
  borderTop: "none",
  zIndex: 15,
};

export const dockedWaveSurferStyles: SxProps<Theme> = {
  width: "100%",
  maxWidth: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: { xs: 36, sm: 42 },
};

export const contextPanelStyles: SxProps<Theme> = (theme) => ({
  flex: { xs: "1 1 58%", sm: "1 1 50%", md: "0 0 38%" },
  width: { xs: "100%", md: "38%" },
  maxWidth: { xs: "100%", md: 420 },
  minHeight: 0,
  maxHeight: { xs: "none", md: "none" },
  display: "flex",
  flexDirection: "column",
  gap: { xs: 0.75, sm: 1, md: 1.5 },
  px: { xs: 1.25, sm: 2, md: 2 },
  py: { xs: 0.75, sm: 1.25, md: 2 },
  pb: {
    xs: "max(8px, env(safe-area-inset-bottom, 0px))",
    sm: "max(10px, env(safe-area-inset-bottom, 0px))",
    md: 2,
  },
  // Match audio stage backdrop so section cards read as highlighted blocks.
  backgroundColor:
    theme.palette.mode === "dark"
      ? "#05070B"
      : theme.palette.grey[100],
  borderLeft: "none",
  borderTop: "none",
  overflow: "hidden",
  color: theme.palette.text.primary,
  [roleplayPhoneLandscapeMedia]: {
    flex: "1 1 58%",
    width: "auto",
    minHeight: 0,
    px: 1,
    py: 0.5,
    gap: 0.5,
    pb: "max(4px, env(safe-area-inset-bottom, 0px))",
  },
});

export const contextSectionStyles = (theme: Theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  flexShrink: 0,
  borderRadius: 2,
  padding: { xs: 1, sm: 1.25, md: 2 },
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "#FFFFFF",
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.14)"
      : "rgba(15, 23, 42, 0.14)"
  }`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 1px 3px rgba(15, 23, 42, 0.05)",
  [roleplayPhoneLandscapeMedia]: {
    padding: 0.75,
    gap: 0.25,
  },
});

// Focus + Status side-by-side on short phones to free transcript height.
export const contextMetaRowStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "row", md: "column" },
  gap: { xs: 0.75, sm: 1, md: 1.5 },
  flexShrink: 0,
  width: "100%",
  minHeight: 0,
  [roleplayPhoneLandscapeMedia]: {
    flexDirection: "row",
    gap: 0.5,
  },
};

export const contextSectionBodyCompactStyles: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 13, sm: 14, md: 16 },
  fontWeight: 500,
  lineHeight: 1.4,
  color: theme.palette.text.primary,
  whiteSpace: "pre-line",
  display: { xs: "-webkit-box", md: "block" },
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: { xs: 2, sm: 3, md: "none" },
  overflow: { xs: "hidden", md: "visible" },
  [roleplayPhoneLandscapeMedia]: {
    WebkitLineClamp: 2,
  },
});

// Transcript block: takes leftover panel height; list inside scrolls.
export const transcriptSectionStyles = (theme: Theme) => ({
  ...contextSectionStyles(theme),
  flex: "1 1 0",
  // Allow shrink on short phones so the list stays on-screen and scrolls.
  minHeight: { xs: 0, sm: 120, md: 160 },
  overflow: "hidden",
  [roleplayPhoneLandscapeMedia]: {
    minHeight: 0,
    flex: "1 1 0",
    padding: 0.75,
  },
});

export const contextSectionTitleStyles: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 11, sm: 12 },
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
});

export const contextSectionBodyStyles: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 14, sm: 15, md: 16 },
  fontWeight: 500,
  lineHeight: 1.45,
  color: theme.palette.text.primary,
  whiteSpace: "pre-line",
});

export const sessionStatusStyles = (color: string): SxProps<Theme> => ({
  fontSize: { xs: 15, sm: 16, md: 18 },
  fontWeight: 700,
  color,
});

export const transcriptPlaceholderStyles: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 13, sm: 14 },
  fontWeight: 400,
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
  fontStyle: "italic",
});

export const transcriptListStyles: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
  flex: "1 1 0",
  minHeight: 0,
  height: "100%",
  overflowY: "auto",
  overflowX: "hidden",
  overscrollBehavior: "contain",
  WebkitOverflowScrolling: "touch",
  pr: 0.75,
  pb: { xs: 1.5, sm: 1 },
  scrollbarWidth: "thin",
  scrollbarColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.28) transparent"
      : "rgba(15,23,42,0.28) transparent",
  "&::-webkit-scrollbar": {
    width: 6,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.28)"
        : "rgba(15,23,42,0.28)",
    borderRadius: 3,
  },
});

export const transcriptLineStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
};

export const transcriptRoleStyles = (isUser: boolean): SxProps<Theme> => (theme) => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: "uppercase",
  color: isUser
    ? theme.palette.mode === "dark"
      ? "#67E8F9"
      : "#0891B2"
    : theme.palette.mode === "dark"
      ? "#FCD34D"
      : "#D97706",
});

export const transcriptTextStyles = (isFinal: boolean): SxProps<Theme> => (theme) => ({
  fontSize: { xs: 13, sm: 14 },
  fontWeight: 400,
  lineHeight: 1.45,
  color: isFinal
    ? theme.palette.text.primary
    : theme.palette.text.secondary,
  fontStyle: isFinal ? "normal" : "italic",
});

export const transcriptSpeakingHintStyles: SxProps<Theme> = (theme) => ({
  fontWeight: 500,
  textTransform: "none",
  letterSpacing: 0,
  color: theme.palette.text.secondary,
});

// Karaoke accent — readable on both light and dark session panels.
export const LIVE_SESSION_AI_BLUE = "#38BDF8";
export const LIVE_SESSION_AI_BLUE_LIGHT = "#0284C7";

export const liveSessionKaraokeTokenStyles = (
  phase: "past" | "current" | "future",
): SxProps<Theme> => (theme) => {
  const isDark = theme.palette.mode === "dark";
  const aiBlue = isDark ? LIVE_SESSION_AI_BLUE : LIVE_SESSION_AI_BLUE_LIGHT;
  const future = isDark ? "rgba(255, 255, 255, 0.42)" : "rgba(15, 23, 42, 0.42)";

  return {
    transition: "color 0.14s ease, opacity 0.14s ease, font-weight 0.14s ease",
    ...(phase === "future" && {
      color: future,
      fontWeight: 400,
      opacity: 1,
    }),
    ...(phase === "past" && {
      color: aiBlue,
      fontWeight: 500,
      opacity: 0.92,
    }),
    ...(phase === "current" && {
      color: aiBlue,
      fontWeight: 700,
      opacity: 1,
    }),
  };
};

export const liveSessionKaraokeBodyStyles: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 13, sm: 14 },
  fontWeight: 400,
  lineHeight: 1.55,
  color: theme.palette.text.primary,
});

export const countdownOverlayStyles: SxProps<Theme> = (theme) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  backgroundColor:
    theme.palette.mode === "dark" ? "#000" : theme.palette.background.default,
});

export const videoContainerStyles: SxProps = {
  width: "100%",
  height: "100%",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const videoStyles = (isConnected: boolean): React.CSSProperties => ({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: isConnected ? "block" : "none",
  position: "absolute",
  top: 0,
  left: 0,
});

export const loadingContainerStyles: SxProps = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
  zIndex: 10,
};

export const loadingProgressStyles: SxProps = {
  color: "#28C9DC",
};

export const initializingContainerStyles: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
};

export const errorContainerStyles: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
  p: 2,
};

export const controlsContainerStyles: SxProps<Theme> = {
  position: "absolute",
  bottom: { xs: 36, sm: 52 },
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: { xs: 0.5, sm: 1 },
  alignItems: "center",
  backgroundColor: "#111827",
  border: "1px solid rgba(255, 255, 255, 0.12)",
  borderRadius: { xs: 1.5, sm: 2 },
  p: { xs: 0.75, sm: 1 },
  zIndex: 15,
};

export const roleplayControlsContainerStyles: SxProps<Theme> = {
  left: { xs: 8, sm: 12 },
  right: { xs: 8, sm: 12 },
  bottom: { xs: 8, sm: 12 },
  width: "auto",
  transform: "none",
  p: 0,
  backgroundColor: "transparent",
  border: "none",
  borderRadius: 0,
  alignItems: "flex-end",
  [roleplayPhoneLandscapeMedia]: {
    left: "max(8px, env(safe-area-inset-left, 0px))",
    right: "max(8px, env(safe-area-inset-right, 0px))",
    bottom: "max(6px, env(safe-area-inset-bottom, 0px))",
  },
};

// Docked under video: controls sit in normal flow, grouped together.
export const dockedRoleplayControlsContainerStyles: SxProps<Theme> = {
  position: "relative",
  left: "auto",
  right: "auto",
  bottom: "auto",
  width: "100%",
  transform: "none",
  p: 0,
  backgroundColor: "transparent",
  border: "none",
  borderRadius: 0,
  alignItems: "center",
  justifyContent: "center",
};

export const getAvatarControlsContainerSx = (
  isRoleplayLayout: boolean,
  docked = false,
): SxProps<Theme> => {
  if (docked) {
    return dockedRoleplayControlsContainerStyles;
  }
  return isRoleplayLayout
    ? ([controlsContainerStyles, roleplayControlsContainerStyles] as SxProps<Theme>)
    : controlsContainerStyles;
};

export const pushToTalkLabelStyles: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 9, sm: 11 },
  fontWeight: 700,
  // Docked under video stage — keep contrast on both themes.
  color: theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.primary,
  opacity: 0.85,
  whiteSpace: "nowrap",
  userSelect: "none",
});

export const muteButtonStyles = (isMuted: boolean): SxProps => ({
  backgroundColor: isMuted ? "#7F1D1D" : "#166534",
  color: "#FFFFFF",
  "&:hover": {
    backgroundColor: isMuted ? "#991B1B" : "#15803D",
  },
});

export const recordingIndicatorStyles: SxProps = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: "red",
  animation: "pulse 1.5s ease-in-out infinite",
  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.5,
    },
  },
};

export const loaderStyles: SxProps = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
  zIndex: 10,
};

export const loaderProgressStyles: SxProps = {
  size: 60,
  sx: {
    color: "#28C9DC",
  },
};

export const warningOverlayStyles: SxProps = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "rgba(0, 0, 0, 0.9)",
  borderRadius: { xs: 1.5, sm: 2 },
  p: { xs: 1.5, sm: 2.5, md: 3 },
  zIndex: 1000,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: { xs: 1, sm: 1.5, md: 2 },
  minWidth: { xs: "calc(100% - 32px)", sm: 400 },
  maxWidth: { xs: "calc(100% - 32px)", sm: "90%", md: 500 },
  mx: { xs: 2, sm: 0 },
};

export const warningTextContainerStyles: SxProps = {
  mb: { xs: 1, sm: 1.5, md: 2 },
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
};

export const warningTextStyles = (
  index: number,
  arrayLength: number
): SxProps => ({
  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
  fontWeight: 600,
  mb: index < arrayLength - 1 ? { xs: 0.75, sm: 1 } : 0,
  textAlign: "center",
  px: { xs: 1, sm: 0 },
  lineHeight: { xs: 1.4, sm: 1.5 },
  width: "100%",
});

export const waveSurferStyles: SxProps = {
  position: "absolute",
  bottom: { xs: 108, sm: 132 },
  left: "50%",
  transform: "translateX(-50%)",
  width: { xs: "calc(100vw - 32px)", sm: 400, md: 500 },
  maxWidth: "90vw",
  backgroundColor: "transparent",
  borderRadius: { xs: 1.5, sm: 2 },
  p: { xs: 0.75, sm: 1 },
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: { xs: 60, sm: 64 },
  zIndex: 10,
  [roleplayPhoneLandscapeMedia]: {
    bottom: 72,
    height: 40,
    width: "min(420px, calc(100vw - 120px))",
  },
};
